import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api, API } from "@/lib/api";
import { downloadPdf } from "@/lib/pdfDownload";
import { Download, ShieldCheck, EyeOff, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { RegulatorAuthority } from "@/components/RegulatorAuthority";
import { regimeLabel } from "@/lib/permittingI18n";
import { memoCountry, memoGrid, memoSector, memoParts } from "@/lib/memoSummary";
import { CondFuelBanner } from "@/components/CondFuelBanner";
import { MemoMeta } from "@/components/MemoMeta";
import { comparisonYears } from "@/lib/comparison";
import { useT, getLocale, tr } from "@/lib/i18n";
import { applicationLabel } from "@/lib/application";
import { roadmapRows, roadmapLeadNote } from "@/lib/roadmap";
import { equipmentRow, capexTech, capexRowLabel, sectorName, frNum } from "@/lib/resultsFr";
import { localisePermitting } from "@/lib/permittingI18n";
import { memoGridFr, memoPartsFr } from "@/lib/memoSummary";
import { num, money, moneyM, pct, unit, perUnit, perYr, NBSP } from "@/lib/numfmt";

const usd = (n) => `${money(n || 0, 0)}`;
const fmt = (n) => num(n || 0, 1);
const kw = (v, u = "kW") => `${fmt(v)}${getLocale() === "fr" ? NBSP : " "}${u}`;
// a37 — only the machines this plant has; solar in kWp.
const PRIORITY_EN = { lowest_lifetime: "lowest lifetime cost", lowest_upfront: "lowest upfront cost",
  lowest_emission: "lowest emissions", max_reliability: "maximum reliability" };
const cleanTitle = (n) => String(n || "").replace(/(^|\s)#(?=\S)/g, "$1");
const thermalRow = (m, kwv) => {
  const units = Number(m.gas_units || 0), u = Number(m.gas_unit_size_kw || 0);
  return units > 0 && u > 0 ? `${units} × ${kw(u)} = ${kw(kwv)}` : kw(kwv);
};
const plantRows = (m, isFr) => [
  m.solar_kw > 0 && ["Solar PV", kw(m.solar_kw, isFr ? "kWc" : "kWp")],
  m.battery_kwh > 0 && ["Battery storage", `${kw(m.battery_kwh, "kWh")} · ${kw(m.battery_power_kw)}`],
  m.genset_kw > 0 && ["Diesel gensets", `${m.genset_units} × ${kw(m.genset_unit_size_kw)} = ${kw(m.genset_kw)}`],
  m.gas_kw > 0 && ["Reciprocating gas engine", thermalRow(m, m.gas_kw)],
  m.ocgt_kw > 0 && ["OCGT (open-cycle gas turbine)", thermalRow(m, m.ocgt_kw)],
  m.ccgt_kw > 0 && ["CCGT (combined-cycle gas turbine)", thermalRow(m, m.ccgt_kw)],
  m.hfo_kw > 0 && ["HFO", kw(m.hfo_kw)],
  m.wind_kw > 0 && ["Wind", kw(m.wind_kw)],
].filter(Boolean);
const lcoeTxt = (v) => (getLocale() === "fr" ? perUnit(v, "kWh", 3, 0) : `$${v}/kWh`);

export const SharedReport = () => {
  const { token } = useParams();
  const t = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/shared/${token}`).then((r) => setData(r.data)).catch((err) => {
      setError(err?.response?.status === 404 ? "not_found" : "generic");
    });
  }, [token]);

  if (error) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-[720px] mx-auto px-6 py-20 text-center" data-testid="shared-error">
          <EyeOff size={40} className="mx-auto" style={{ color: "var(--muted)" }} />
          <h1 className="font-display text-3xl mt-4">{tr("Link not available", "Lien indisponible")}</h1>
          <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
            {tr("The share link has expired, been revoked or never existed.", "Ce lien de partage a expiré, a été révoqué ou n'a jamais existé.")}
          </p>
          <Link to="/" className="btn-primary mt-6 inline-flex">{tr("Go to Power Configurator", "Aller au configurateur")} <ArrowRight size={14} /></Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-[720px] mx-auto px-6 py-20 text-center" style={{ color: "var(--muted)" }}>{tr("Loading shared report…", "Chargement du rapport partagé…")}</div>
      </div>
    );
  }

  const p = data.project;
  const input = p.input || {};
  const r = p.result || {};
  const m = r.model || {};
  const cap = r.capex || {};
  const opex = r.opex || {};
  const fin = r.financials || {};
  const permits = r.permitting || {};
  const milestones = r.milestones || [];
  const sched = r.schedule || {};
  const leadNote = roadmapLeadNote(sched, t);
  const eq = (r.equipment || []).map(equipmentRow);
  const isFr = getLocale() === "fr";
  const lang = isFr ? "fr" : "en";
  const prio = isFr ? t(`wiz.priority.${input.priority}`, input.priority) : input.priority?.replace(/_/g, " ");
  const prioInline = prio ? prio.charAt(0).toLowerCase() + prio.slice(1) : prio;
  const country = isFr ? t(`country.${input.country}`, input.country) : input.country;
  const shownPermits = localisePermitting(permits, lang);
  const views = data.view_count;

  return (
    <div className="App" style={{ background: "var(--paper)" }}>
      <Header />
      <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-10">
        <div className="card p-5 mb-6 flex flex-wrap items-center justify-between gap-3" style={{ background: "#fff8f2", borderColor: "#ffd7c2" }} data-testid="shared-banner">
          <div className="flex items-center gap-3 text-sm">
            <ShieldCheck size={16} style={{ color: "var(--brand-green)" }} />
            <div>
              <div className="font-display text-sm" style={{ color: "var(--charcoal)" }}>{tr("Read-only shared view", "Vue partagée en lecture seule")}</div>
              <div style={{ color: "var(--muted)" }}>
                {tr("Shared by", "Partagé par")} <b>{data.shared_by?.name || tr("the owner", "le propriétaire")}</b>
                {data.shared_by?.email_domain ? ` · ${data.shared_by.email_domain}` : ""} · {isFr
                  ? `Consulté ${views}\u00a0fois`
                  : `Viewed ${views} time${views === 1 ? "" : "s"}`}
              </div>
            </div>
          </div>
          <a
            href={`${API}/shared/${token}/pdf`}
            className="btn-primary"
            data-testid="shared-download-pdf"
            onClick={(e) => { e.preventDefault(); downloadPdf(`${API}/shared/${token}/pdf`, "Power_Configurator_report.pdf"); }}
          >
            <Download size={13} /> {tr("Download PDF", "Télécharger le PDF")}
          </a>
        </div>

        <div className="card p-8 md:p-12" style={{ background: "var(--white)" }} data-testid="shared-doc">
          <div className="pb-6 mb-8" style={{ borderBottom: "2px solid var(--charcoal)" }}>
            <div className="overline">{tr("Feasibility memorandum · Screening-level", "Mémorandum de faisabilité · Niveau préliminaire")}</div>
            <h1 className="font-display text-4xl mt-2">{cleanTitle(p.name)}</h1>
            <MemoMeta input={input} m={m} prio={prio} country={country} t={t} />
          </div>

          <CondFuelBanner result={r} />
          <Section n="01" title={tr("Executive summary", "Synthèse")}>
            {isFr ? (<>
              <p>
                Ce mémorandum présente un cadrage indicatif, de niveau préliminaire, pour une application du secteur
                {" "}<b>{sectorName(input.sector)}</b> — pays{"\u00a0"}: <b>{country}</b>.
                Le profil du site indique une pointe de charge de <b>{kw(input.peak_kw)}</b> (capacité ferme cible avec 20{"\u00a0"}% de réserve{"\u00a0"}: <b>{kw(m.firm_capacity_target_kw)}</b>)
                et une demande annuelle de <b>{kw(m.annual_demand_kwh || m.annual_energy_kwh, "kWh")}</b>, en fonctionnement
                {" "}<b>{memoGridFr(input.grid_status)}</b>.
                {" "}La centrale recommandée utilise {memoPartsFr(m)}, choisie pour la priorité{"\u00a0"}: <b>{prioInline}</b>.
              </p>
              <p className="mt-2">
                CAPEX indicatif <b>{usd(cap.total)}</b> (fourchette {usd(cap.range_low)}–{usd(cap.range_high)}),
                LCOE <b>{lcoeTxt(fin.lcoe_usd_per_kwh)}</b>, retour sur investissement simple par rapport au cas de
                comparaison{"\u00a0"}: <b>{comparisonYears(fin.simple_payback_years, "fr")}</b>.
              </p>
            </>) : (<>
            <p>
              This memorandum presents an indicative, screening-level scope for {memoSector(input.sector).a}
              {" "}<b>{memoSector(input.sector).sector}</b> application in <b>{memoCountry(input.country)}</b>.
              The site profile indicates a peak load of <b>{fmt(input.peak_kw)} kW</b> (firm capacity target with 20% reserve: <b>{fmt(m.firm_capacity_target_kw)} kW</b>)
              and annual demand of <b>{fmt(m.annual_demand_kwh || m.annual_energy_kwh)} kWh</b>, operating
              {" "}<b>{memoGrid(input.grid_status)}</b>.
              {" "}The recommended plant uses {memoParts(m)}, chosen for the priority:{" "}
              <b>{PRIORITY_EN[input.priority] || input.priority?.replace(/_/g, " ")}</b>.
            </p>
            <p className="mt-2">
              Indicative CAPEX <b>{usd(cap.total)}</b> (range {usd(cap.range_low)}–{usd(cap.range_high)}),
              LCOE <b>${fin.lcoe_usd_per_kwh}/kWh</b>, simple payback against the comparison case: <b>{comparisonYears(fin.simple_payback_years)}</b>.
            </p>
            </>)}
          </Section>

          <Section n="02" title={tr("Optimised power model", "Modèle énergétique optimisé")}>
            <Table rows={[
              ...plantRows(m, isFr),
              ["Annual demand", kw(m.annual_demand_kwh || m.annual_energy_kwh, "kWh")],
              ["Annual served", kw(m.annual_served_kwh || m.annual_energy_kwh, "kWh")],
              ["Renewable fraction", pct(Math.round((m.renewable_fraction || 0) * 100), 0)],
              ["Firm capacity (target / available)", `${fmt(m.firm_capacity_target_kw)} / ${kw(m.firm_capacity_available_kw)}`],
            ]} />
          </Section>

          <Section n="03" title={tr("Equipment configuration", "Configuration des équipements")}>
            <Table rows={eq.map((e) => [e.item + (e.size ? ` — ${e.size}` : ""), e.category])} />
          </Section>

          <Section n="04" title={tr("Budgetary costs", "Coûts budgétaires")}>
            <Table rows={[
              ...(cap.breakdown || []).map((b) => [capexRowLabel(b), usd(b.capex_usd)]),
              ["Total CAPEX", `${usd(cap.total)}  [${usd(cap.range_low)} – ${usd(cap.range_high)}]`],
              ["Annual OPEX", usd(opex.annual)],
              ["Indicative LCOE", lcoeTxt(fin.lcoe_usd_per_kwh)],
              ["Payback", comparisonYears(fin.simple_payback_years, lang)],
            ]} />
          </Section>

          <Section n="05" title={`${tr("Permitting & regulation", "Autorisations et réglementation")} — ${shownPermits.country ? (isFr ? t(`country.${shownPermits.country}`, shownPermits.country) : shownPermits.country) : country}`}>
            <p className="mb-3 text-sm" style={{ color: "var(--muted)" }}>{tr("Regime:", "Régime\u00a0:")} {regimeLabel(permits.regime, lang)}</p>
            <div className="overflow-x-auto max-w-full" data-testid="permit-table-wrap">
            <table className="w-full text-sm" style={{ borderCollapse: "collapse", tableLayout: "fixed", overflowWrap: "anywhere" }} data-testid="permit-table">
              <tbody>
                {(shownPermits.items || []).map((it, i) => (
                  <tr key={i} style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
                    <td className="py-2.5 pr-4 align-top" style={{ color: "var(--body)" }}>
                      {it.item}{it.notes ? <span style={{ color: "var(--muted)" }}> — {it.notes}</span> : null}
                    </td>
                    <td className="py-2.5 align-top text-right">
                      <span style={{ color: "var(--muted)" }}>{it.status}</span>
                      {it.authority && (
                        <>
                          <span style={{ color: "var(--muted)" }}> · </span>
                          <RegulatorAuthority authority={it.authority} meta={it.regulator_meta} />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Section>

          <Section n="06" title={tr("Initial project & financial plan", "Plan initial de projet et de financement")}>
            <h4 className="font-display text-base mb-3">{t("report.roadmap.title")}</h4>
            <Table rows={roadmapRows(milestones, sched, t)} />
            {leadNote && (
              <p className="text-xs mt-3" style={{ color: "var(--muted)" }} data-testid="shared-roadmap-lead-note">
                {leadNote}
              </p>
            )}
          </Section>

          <div className="mt-10 pt-6" style={{ borderTop: "1px solid var(--hairline)" }}>
            <p className="text-xs" style={{ color: "var(--muted)" }}>
              {tr(`The Power Configurator provides indicative, screening-level outputs for information only and does not
              constitute engineering, financial or investment advice.`,
                "Le configurateur fournit des résultats indicatifs, de niveau préliminaire, à titre informatif uniquement, et ne constitue pas un conseil d'ingénierie, financier ou d'investissement.")}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <div className="mt-8">
    <div className="flex items-baseline gap-3 mb-3">
      <span className="font-mono text-xs" style={{ color: "var(--brand-green)" }}>{n}</span>
      <h2 className="font-display text-xl">{title}</h2>
    </div>
    <div className="text-sm" style={{ color: "var(--body)" }}>{children}</div>
  </div>
);

// Table row labels stay English in source; French is looked up at render.
const ROW_FR = {
  "Solar PV": "Solaire PV", "Battery storage": "Stockage batterie", "Diesel gensets": "Groupes diesel", Wind: "Éolien",
  "OCGT (open-cycle gas turbine)": "OCGT (turbine à gaz à cycle ouvert)", "CCGT (combined-cycle gas turbine)": "CCGT (turbine à gaz à cycle combiné)",
  "Reciprocating gas engine": "Moteur alternatif à gaz", "Annual demand": "Demande annuelle",
  "Annual served (dispatch)": "Énergie servie annuelle (dispatch)", "Annual served": "Énergie servie annuelle",
  "Annual generation (gross)": "Production annuelle (brute)", "Renewable fraction": "Part renouvelable",
  "Firm capacity (target / available)": "Capacité ferme (cible / disponible)", "Firm capacity basis": "Base de la capacité ferme",
  "Generator derate": "Déclassement des groupes", "PV specific yield": "Productible spécifique PV",
  "Solar O&M": "O&M solaire", "Battery O&M": "O&M batterie", "Grid import": "Achats au réseau", "Total annual OPEX": "OPEX annuel total",
  "Indicative LCOE": "LCOE indicatif", "Discount rate": "Taux d'actualisation",
  "Diesel-only baseline CAPEX": "CAPEX de la référence diesel seul", "Diesel-only baseline OPEX (annual)": "OPEX de la référence diesel seul (annuel)",
  "Annual savings vs diesel-only (Y1)": "Économies annuelles vs diesel seul (A1)", "Simple payback": "Retour sur investissement simple",
  "Equity (~30%)": "Fonds propres (~30\u00a0%)", "Senior debt (~55%)": "Dette senior (~55\u00a0%)", "Grants / mezzanine (~15%)": "Subventions / mezzanine (~15\u00a0%)",
  "Total CAPEX": "CAPEX total", "Annual OPEX": "OPEX annuel", Payback: "Retour sur investissement",
};
const rowLabel = (k) => (getLocale() === "fr" ? ROW_FR[k] ?? k : k);

const Table = ({ rows }) => (
  <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
    <tbody>
      {rows.map(([k, v]) => (
        <tr key={String(k)} style={{ borderBottom: "1px solid var(--hairline)" }}>
          <td className="py-2 pr-4" style={{ color: "var(--body)" }}>{rowLabel(k)}</td>
          <td className="py-2 text-right font-mono" style={{ color: "var(--charcoal)" }}>{v}</td>
        </tr>
      ))}
    </tbody>
  </table>
);
